import { Color } from '@common/types/radix';
import { IconProps } from '@radix-ui/themes';
import React from 'react';

type RadixIconComponent = React.ForwardRefExoticComponent<
    IconProps & React.RefAttributes<SVGSVGElement>
>;

export type StateConfig<T extends string> = Record<
    T,
    {
        icon?: RadixIconComponent;
        label: string;
        color?: Color;
    }
>;

interface UseMultiStateProps<T extends string> {
    states: StateConfig<T>;
    initialState: T;
    order?: T[];
    onChange?: (newState: T) => void;
}

export const useMultiState = <T extends string>({
    states,
    initialState,
    order,
    onChange,
}: UseMultiStateProps<T>) => {
    const [state, setState] = React.useState<T>(initialState);

    const updateState = React.useCallback(
        (next: T) => {
            setState(next);
            onChange?.(next);
        },
        [onChange],
    );

    const cycle = React.useCallback(() => {
        if (!order) return;
        const currentIndex = order.indexOf(state);
        const nextIndex = (currentIndex + 1) % order.length;
        updateState(order[nextIndex]);
    }, [order, state, updateState]);

    const current = React.useMemo(() => states[state], [states, state]);

    return {
        state,
        setState: updateState,
        cycle,
        Icon: current.icon,
        label: current.label,
        buttonProps: {
            onClick: cycle,
            title: current.label,
            'aria-label': current.label,
            color: current.color,
        },
    };
};
